import StateObserver from 'src/StateObserver';
import { isActionSequenceWorking } from './stateUtils';
import { setActionSequenceWorking } from 'src/state/ui';
import { captureGenericError } from 'src/lib/sentry';
import { trackSequenceFinish, trackSequenceStart } from './analytics/events';

type SimpleAction = () => Promise<boolean>;
type ActionWithId = {
  id: string;
  requirePayments: boolean;
  execute: SimpleAction;
};

export type FullSequenceId =
  | 'Invite'
  | 'Launch'
  | 'AdminMessages'
  | 'StartMinigame:match3'
  | 'LevelUp'
  | 'OpenProfile'
  | 'VisitFriend'
  | 'Logout';
export type Action = ActionWithId | SimpleAction;
export type Actions = Array<Action>;

// Inserted actions can't cancel the queue.
type InsertedAction = () => Promise<void>;

export function wrapAction(action: ActionWithId | SimpleAction): ActionWithId {
  if ('id' in action) {
    return action;
  }

  return {
    id: null,
    requirePayments: false,
    execute: action,
  };
}

export class ActionSequence {
  private static queue: Actions[] = [];
  private static queueIds: FullSequenceId[] = [];
  private static currentSequenceId = 'unknown';
  private static insertedActions: InsertedAction[] = [];

  static async start(actions: Actions, sequenceId: FullSequenceId) {
    if (isActionSequenceWorking()) {
      // Prevent running same sequence twice
      if (sequenceId === this.currentSequenceId) {
        throw new Error(`Starting '${sequenceId}' action sequence twice`);
      }

      // Add sequence to queue
      this.queue.push(actions);
      this.queueIds.push(sequenceId);
      return;
    }

    StateObserver.dispatch(
      setActionSequenceWorking({ working: true, sequenceId }),
    );

    try {
      await this.executeActions(actions, sequenceId);
    } finally {
      StateObserver.dispatch(setActionSequenceWorking({ working: false }));
    }
  }

  private static async executeActions(
    actions: Actions,
    sequenceId: FullSequenceId,
  ) {
    trackSequenceStart(sequenceId);

    // Update current sequence id for parallel sequences error above
    this.currentSequenceId = sequenceId;

    for (const step of actions) {
      const action = wrapAction(step);

      if (action.requirePayments) {
        continue;
      }

      const shouldStop = await action.execute().catch((err) => {
        captureGenericError('ActionSequence crash', err);
      });

      await this.executeInsertedActions();

      if (shouldStop) {
        break;
      }
    }

    trackSequenceFinish(sequenceId);

    // Start actions it queue
    if (this.queue.length) {
      await this.executeActions(this.queue.shift(), this.queueIds.shift());
    }
  }

  private static async executeInsertedActions() {
    while (this.insertedActions.length) {
      const insertedAction = this.insertedActions.shift();
      await insertedAction();
    }
  }

  static insertAfterCurrentAction(insertedAction: () => Promise<void>) {
    if (!StateObserver.getState().ui.actionSequenceWorking) {
      throw new Error('No current action to insert after.');
    }

    this.insertedActions.push(insertedAction);
  }

  static insertSequenceAfterCurrentAction(actions: Actions) {
    if (!StateObserver.getState().ui.actionSequenceWorking) {
      throw new Error('No current action to insert after.');
    }

    for (const rawAction of actions) {
      this.insertedActions.push(async () => {
        const action = wrapAction(rawAction);

        if (action.requirePayments) {
          return;
        }

        await action.execute().catch((err) => {
          captureGenericError('Inserted ActionSequence crash', err);
        });
      });
    }
  }
}
